import { Component, type ErrorInfo, type ReactNode } from "react";

interface ChunkErrorBoundaryProps {
  children: ReactNode;
}

interface ChunkErrorBoundaryState {
  hasError: boolean;
  isChunkLoadError: boolean;
}

/**
 * Catches ChunkLoadError — thrown when a lazy-loaded route's JS chunk 404s
 * because a newer deploy has replaced it with differently-hashed files —
 * and reloads immediately instead of showing a broken UI, since there's
 * nothing on screen worth preserving for that case (the app is just trying
 * to load code that no longer exists on the server).
 *
 * Any other render error gets a minimal fallback instead of an auto-reload:
 * reloading unconditionally on every caught error would turn a real bug
 * into an infinite reload loop.
 */
export default class ChunkErrorBoundary extends Component<
  ChunkErrorBoundaryProps,
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = {
    hasError: false,
    isChunkLoadError: false,
  };

  static getDerivedStateFromError(error: Error): ChunkErrorBoundaryState {
    return {
      hasError: true,
      isChunkLoadError: error.name === "ChunkLoadError",
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (error.name === "ChunkLoadError") {
      window.location.reload();
      return;
    }
    console.error("Unhandled render error:", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // The chunk-load case reloads immediately in componentDidCatch above
      // — render nothing while that happens rather than flashing a fallback.
      if (this.state.isChunkLoadError) return null;

      return (
        <div className="d-flex flex-column align-items-center justify-content-center text-center p-5">
          <h4>Something went wrong</h4>
          <p className="text-muted">Please reload the page and try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
